import { SiteTemplate } from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue } from '@on3/ui-lib/utils/api';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import {
  HighSchoolHome,
  type IHighSchoolHomeProps,
} from 'components/Home/Templates/HighSchoolHome';
import { RivalsHome } from 'components/Home/Templates/RivalsHome';
import {
  type ITraditionalHomeProps,
  TraditionalHome,
} from 'components/Home/Templates/TraditionalHome';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';
import { GraphSchema } from 'store/schemas/Graph';
import { OrganizationSchema } from 'store/schemas/Organization';
import { ScheduleSchema } from 'store/schemas/Schedule';
import { createCanonicalUrl, createDescription, createTitle } from 'utils/meta';
import { getSiteDataByTemplateType, getSiteState } from 'utils/sites';

interface ISiteResponse
  extends IGenericPageProps,
    ITraditionalHomeProps,
    IHighSchoolHomeProps {}

export const TeamPage = ({ siteData, ...props }: ISiteResponse) => {
  const { currentSite } = siteData || {};
  const { template } = currentSite || {};
  const { organization, siteName, url: siteUrl } = currentSite || {};
  const orgName = organization?.knownAs ?? organization?.name;

  const title = createTitle(
    siteName && `${siteName}:`,
    orgName,
    organization?.mascot,
    'Football & Basketball',
  );
  const description = createDescription(
    `The latest {0} {1} news, recruiting, and NIL information at {2}, part of on3.com`,
    orgName,
    organization?.mascot,
    siteName,
  );
  const canonical = createCanonicalUrl(siteUrl as string);

  const schemaItems = [
    new OrganizationSchema().toJSON(),
    new OrganizationSchema({
      name: orgName || '',
      url: canonical,
      logo: {
        url: getAssetUrl(organization?.defaultAssetRes) || '',
        width: organization?.defaultAssetRes?.width || 0,
        height: organization?.defaultAssetRes?.height || 0,
      },
    }).toJSON(),
  ];

  if (props.schedule) {
    schemaItems.push(
      new ScheduleSchema({
        name: organization?.name || '',
        url: canonical,
        sport: props.schedule.relatedModel?.sport?.name || '',
        event: props.schedule.list || [],
      }).toJSON(),
    );
  }

  const schema = new GraphSchema({
    items: schemaItems,
  }).toJSON();

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      schema={schema}
      siteData={siteData}
      title={title}
    >
      {template === SiteTemplate.Traditional && <TraditionalHome {...props} />}
      {template === SiteTemplate.HighSchool && <HighSchoolHome {...props} />}
      {template === SiteTemplate.Rivals && <RivalsHome {...props} />}
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { currentSite, defaultYears } = siteData;

  if (!currentSite) {
    return {
      notFound: true,
    };
  }

  const state = getSiteState(currentSite.key);

  try {
    const data = {
      siteData,
      userData: userData || {},
    };

    const promises = getSiteDataByTemplateType(
      currentSite.template,
      currentSite,
      {
        year: defaultYears?.footballRecruitingYear,
        stateAbbr: state?.abbreviation,
        stateKey: state?.key,
        sportKey: 1, // Default to football
      },
    );

    // Fetch all site data in parallel here
    const resolvedPromises = await Promise.allSettled(
      Object.values(promises).map((promise) =>
        promise ? promise() : Promise.resolve(null),
      ),
    );

    resolvedPromises.forEach((promise, i) => {
      const key = Object.keys(promises)[i];

      Object.assign(data, { [key]: getFulfilledValue(promise) });
    });

    return { props: { ...data, pageType: 'home' } };
  } catch (e) {
    // console.log(e);
    return { props: {} };
  }
};

export default withValidUrl(TeamPage);
